/**
 * Schema-management scheduled-draft edit + config-diff loader.
 *
 * `mergeConfigChanges` produces the summary-review bundle used by
 * loadScheduledDraftConfig.
 * `fetchScheduledDraftConfig` fetches either a scheduled or drafted config
 * from the adapter (commercial-aware).
 * `editScheduledDraft` loads an existing schedule/draft into the editor,
 * navigating to the first modified section on success.
 * `checkForExistentScheduledDraftConfig` sets the name/liveDate uniqueness
 * errors on the summary form.
 */

import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import { diff } from "deep-object-diff";
import { getStore } from "@store";
import schemaManagementAdapter from "@features/schema-management/adapters";
import { camelToTitle, toKebabCase, toSentenceCase } from "@utils/string-helpers";
import { isEmptyObject } from "@utils/object-helpers";
import {
  logDiffs,
  prepareArrayPaths,
} from "@features/schema-management/utils/schemaManagementHelpers";
import { SYF_CARD_ACCESS, SYPI_BRC, SCHEDULE_DRAFT_SAVE_STATE } from "@constants/schema-management";
import {
  showSpinner,
  stopSpinner,
  enableSave,
  setError,
  clearLog,
  clearErrors,
  recordChangeObject,
  logChangedKey,
} from "@actions/global";
import {
  setSMConfigSaveState,
  setReleaseVersion,
  setInitialReleaseVersion,
  setDefaultReleaseVersion,
  loadScheduledDraft,
  loadInitialScheduledDraft,
  loadConfig,
  setDeletedClients,
  enableSummaryPostAction,
} from "@features/schema-management/actions";
import { modifySchema } from "./sm-shared";
import { sendFinalReport } from "./sm-summary";

/** @TODO verify that we can delete func */
export const mergeConfigChanges = (
  changeLog,
  initialSypiConfig,
  logAdditionalDetails = {}
) => {
  const updatedChangeLog = Object.keys(changeLog);

  const summaryReview = [];
  updatedChangeLog.forEach((change) => {
    const modifiedSection = camelToTitle(change.split(".")[0]);

    const newKeyValue = changeLog[change];
    const oldKeyValue = get(initialSypiConfig, change);

    const updatedNewKeyValue = newKeyValue
      ? Array.isArray(newKeyValue)
        ? newKeyValue.join(", ")
        : typeof newKeyValue === "object"
          ? Object.keys(newKeyValue).join(", ")
          : newKeyValue
      : "";
    const updatedOldKeyValue = oldKeyValue
      ? Array.isArray(oldKeyValue)
        ? oldKeyValue.join(", ")
        : typeof oldKeyValue === "object"
          ? Object.keys(oldKeyValue).join(", ")
          : oldKeyValue
      : "";
    if (updatedNewKeyValue !== updatedOldKeyValue)
      summaryReview.push({
        section: modifiedSection,
        keyName: change,
        newKeyValue: updatedNewKeyValue,
        oldKeyValue: updatedOldKeyValue,
        ...(logAdditionalDetails && logAdditionalDetails[change]
          ? logAdditionalDetails[change]
          : {}),
      });
  });
  return summaryReview;
};

export const fetchScheduledDraftConfig = async (
  paramConfig,
  isScheduled,
  store = getStore()
) => {
  const { featureName } = store.getState().schemaAllView;
  const isCommercial = featureName === SYPI_BRC;
  return isScheduled
    ? await schemaManagementAdapter.fetchScheduledConfig(
        paramConfig,
        isCommercial
      )
    : await schemaManagementAdapter.fetchDraftConfig(paramConfig, isCommercial);
};

export const editScheduledDraft = async (
  isScheduled,
  editConfig,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  let displayStatus = isScheduled ? "Schedule" : "Draft";
  const { release_version } = editConfig;
  const { featureName, isAthosConfig } = store.getState().schemaAllView;
  let finalReport = null;
  store.dispatch(setSMConfigSaveState(SCHEDULE_DRAFT_SAVE_STATE.EDIT));
  store.dispatch(setReleaseVersion(release_version));
  store.dispatch(setInitialReleaseVersion(release_version));
  store.dispatch(setDefaultReleaseVersion(release_version));
  let fetchResult = await fetchScheduledDraftConfig(editConfig, isScheduled);
  let { apiError, configSchedules } = fetchResult;
  if (apiError && apiError.details) {
    finalReport = {
      type: "ALERT_DOCK",
      title: `Loading the ${toSentenceCase(
        apiError.details
      )} draft was NOT successful. `,
      message:
        "Please contact the application development team for assistance.",
    };
  } else {
    if (configSchedules && configSchedules.length) {
      const smchedulerConfig = configSchedules.find(
        (config) => config.release_version === release_version
      );
      store.dispatch(loadScheduledDraft(smchedulerConfig));
      store.dispatch(loadInitialScheduledDraft(smchedulerConfig));
      if (
        smchedulerConfig &&
        smchedulerConfig.schemaPath &&
        !isEmptyObject(smchedulerConfig.schemaPath)
      ) {
        const { sectionsModified, schemaPath } = smchedulerConfig;
        const { smS3 } = schemaPath;
        loadSMVersionDetails("smS3", smS3, sectionsModified);
        const redirectTo =
          sectionsModified && sectionsModified.length
            ? featureName === SYF_CARD_ACCESS || isAthosConfig
              ? {
                  section: `schema-management-${toKebabCase(featureName)}`,
                }
              : {
                  section: `schema-management-${toKebabCase(
                    featureName + "-" + sectionsModified[0]
                  )}`,
                }
            : {};

        finalReport = {
          type: "TOAST_MESSAGE",
          message: `${displayStatus} changes loaded `,
          redirect: `/${redirectTo.section}`,
        };
      }
    }
  }
  store.dispatch(enableSave());
  store.dispatch(stopSpinner());
  sendFinalReport(finalReport);
};

// Internal: reconstructs the client config from a saved smS3 schemaPath
// payload, then diffs it against `initialConfig` to record the changes.
// Athos configs also propagate new-client entries into the schema.
const loadSMVersionDetails = (
  feature,
  smPayload,
  newClients,
  store = getStore()
) => {
  if (feature === "smS3") {
    const { featureName, initialConfig, isAthosConfig, existingClients } =
      store.getState().schemaAllView;
    const isCardAccessSchema = featureName === SYF_CARD_ACCESS;
    const deletedClients = {};
    const formattedPaths = isCardAccessSchema
      ? prepareArrayPaths(smPayload, initialConfig)
      : Object.keys(smPayload);
    const clonedInitialConfig = cloneDeep(initialConfig);
    const updatedSmConfig = formattedPaths.length
      ? Object.keys(smPayload).reduce((accConfig, path, i) => {
          if (path) {
            const pathIsClient =
              existingClients.includes(path) &&
              (isCardAccessSchema || isAthosConfig);
            if (pathIsClient && smPayload[path] === null)
              deletedClients[path] = null;
            else set(accConfig, formattedPaths[i], smPayload[path]);
          }
          return accConfig;
        }, clonedInitialConfig)
      : clonedInitialConfig;

    let loggedDiffs = logDiffs(initialConfig, updatedSmConfig, featureName);
    if (isCardAccessSchema || isAthosConfig) {
      loggedDiffs = { ...loggedDiffs, ...deletedClients };
      store.dispatch(setDeletedClients(deletedClients));
      if (isAthosConfig) {
        newClients.forEach((client) => modifySchema(client));
      }
    }
    store.dispatch(clearLog());
    store.dispatch(clearErrors());
    store.dispatch(recordChangeObject(loggedDiffs));
    store.dispatch(logChangedKey(Object.keys(loggedDiffs)));
    store.dispatch(loadConfig(updatedSmConfig, false));
  }
};

export const checkForExistentScheduledDraftConfig = async (
  scheduleDraft,
  initialScheduleDraft,
  activeReleaseVersion,
  summaryViewType,
  store = getStore()
) => {
  const { status, name, liveScheduleDate } = scheduleDraft;
  const isScheduled = status === "scheduled";
  let fetchResult = {};
  if (
    (isScheduled &&
      initialScheduleDraft.liveScheduleDate !== liveScheduleDate) ||
    (!isScheduled && initialScheduleDraft.name !== name)
  ) {
    fetchResult =
      (!isScheduled && name) || liveScheduleDate
        ? await fetchScheduledDraftConfig(
            isScheduled
              ? {
                  live_schedule_date: liveScheduleDate,
                  release_version: activeReleaseVersion,
                }
              : { name: name, release_version: activeReleaseVersion },
            isScheduled
          )
        : {};
  }
  const { configSchedules } = fetchResult;
  let schedduleDraftError = null;
  if (
    configSchedules &&
    configSchedules?.length &&
    summaryViewType === "form"
  ) {
    const scheduledDraftChanges = diff(initialScheduleDraft, scheduleDraft);
    schedduleDraftError = configSchedules.reduce(
      (acc, config) => {
        acc.nameError =
          acc.nameError || (scheduledDraftChanges && scheduledDraftChanges.name)
            ? name === config.name
            : false;
        acc.liveDateError =
          acc.liveDateError ||
          (scheduledDraftChanges && scheduledDraftChanges.liveScheduleDate)
            ? liveScheduleDate === config.liveScheduleDate
            : false;
        return acc;
      },
      { nameError: false, liveDateError: false }
    );
  }

  store.dispatch(
    setError({
      errorName: "uniqueScheduleDraftName",
      errorPresent: schedduleDraftError?.nameError,
    })
  );
  store.dispatch(
    setError({
      errorName: "uniqueLiveScheduleDateError",
      errorPresent: schedduleDraftError?.liveDateError,
    })
  );
  store.dispatch(
    enableSummaryPostAction(
      schedduleDraftError?.nameError ||
        schedduleDraftError?.liveDateError ||
        (!isScheduled && !!name) ||
        (isScheduled && !!name && !!liveScheduleDate)
    )
  );
};
